/**
 * Lobby scene — three-column layout matching WhatIs:
 *   left:  TOP 10 leaderboard panel + HOW TO PLAY card
 *   center: galloping-horse hero illustration + "HORSE RACE" title
 *   right: scrolling all-time STANDINGS panel (ranks 11+)
 *
 * Visual upgrade (Phase 3):
 *   - Drops the procedural SVG horse and uses the Leonardo-generated
 *     `title-horse.png` asset (id `hr_title_horse`) — a real painted
 *     galloping racehorse rather than the cartoon SVG.
 *   - Wraps the PNG in a CSS dust-trail layer + bob animation so the
 *     hero still feels alive without depending on raw SVG paths.
 */

import type { LbRow } from '@sg/shared';
import {
  createLobbyLeaderboardTop,
  createLobbyLeaderboardTail,
  type LobbyLeaderboardHandle,
} from '../ui/lobby-leaderboard.js';
import { createLobbyHowTo } from '../ui/lobby-howto.js';
import { fitText } from '../ui/fit-text.js';
import titleHorseUrl from '../assets/title-horse.png?url';

export interface LobbyHandle {
  el: HTMLDivElement;
  setLeaderboard(rows: LbRow[], allTime: LbRow[]): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes hr-lobby-pulse {
    0%, 100% { transform: translateY(0) scale(1); }
    50%      { transform: translateY(-8px) scale(1.02); }
  }
  @keyframes hr-lobby-glow {
    0%, 100% { text-shadow: 0 6px 0 var(--sg-secondary, #3B0764), 0 0 60px rgba(255,196,61,0.35); }
    50%      { text-shadow: 0 6px 0 var(--sg-secondary, #3B0764), 0 0 90px rgba(255,196,61,0.7);  }
  }
  @keyframes hr-horse-bob {
    0%, 100% { transform: translateY(0) rotate(-1deg); }
    50%      { transform: translateY(-14px) rotate(1.5deg); }
  }
  @keyframes hr-horse-dust {
    0%, 100% { opacity: 0.55; transform: translateX(0)   scaleX(1);   }
    50%      { opacity: 0.85; transform: translateX(-8px) scaleX(1.1); }
  }
  .hr-lobby {
    position: absolute; inset: 0;
    display: grid;
    grid-template-columns: 380px 1fr 380px;
    gap: 28px;
    padding: 80px 56px 56px 56px;
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    box-sizing: border-box;
    z-index: 2;
  }
  .hr-lobby-col {
    display: flex; flex-direction: column;
    gap: 24px;
    min-height: 0;
  }
  .hr-lobby-center {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center;
    position: relative;
    gap: 6px;
  }
  .hr-lobby-horse-wrap {
    position: relative;
    width: 720px; height: 432px;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: -8px;
  }
  .hr-lobby-horse {
    position: relative; z-index: 2;
    width: 100%; height: 100%;
    object-fit: contain;
    filter:
      drop-shadow(0 22px 36px rgba(0,0,0,0.6))
      drop-shadow(0 0 60px rgba(255,196,61,0.18));
    animation: hr-horse-bob 3.4s ease-in-out infinite;
    transform-origin: 60% 80%;
  }
  .hr-lobby-horse-glow {
    position: absolute;
    inset: 12% 5% -4% 5%;
    z-index: 1;
    background: radial-gradient(ellipse at 50% 70%, rgba(255,196,61,0.30) 0%, rgba(255,196,61,0) 65%);
    pointer-events: none;
  }
  .hr-lobby-horse-dust {
    position: absolute;
    z-index: 3;
    bottom: 12%; left: 4%;
    width: 30%; height: 18%;
    pointer-events: none;
    animation: hr-horse-dust 1.2s ease-in-out infinite;
    transform-origin: center;
  }
  .hr-lobby-title {
    font-family: 'Bebas Neue', 'Oswald', sans-serif;
    font-weight: 700;
    color: var(--sg-primary, #FFC43D);
    line-height: 0.92;
    letter-spacing: 0.06em;
    animation: hr-lobby-pulse 4s ease-in-out infinite, hr-lobby-glow 3.2s ease-in-out infinite;
  }
  .hr-lobby-title-1 { font-size: 160px; }
  .hr-lobby-title-2 { font-size: 280px; letter-spacing: 0.12em; }
  .hr-lobby-sub {
    margin-top: 26px;
    font-size: 28px;
    color: #E2E8F0;
    opacity: 0.92;
    max-width: 760px;
    line-height: 1.4;
  }
  .hr-lobby-sub code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.16);
    padding: 2px 10px;
    border-radius: 6px;
    font-family: ui-monospace, monospace;
    font-size: 0.92em;
    margin: 0 2px;
  }
  .hr-lobby-tagline {
    margin-top: 18px;
    font-family: 'Oswald', sans-serif;
    font-size: 22px;
    letter-spacing: 0.32em;
    color: var(--sg-accent, #22C55E);
    text-transform: uppercase;
    text-shadow: 0 0 18px rgba(34,197,94,0.4);
  }
`;

function ensureStyles(): void {
  if (document.getElementById('hr-lobby-style')) return;
  const s = document.createElement('style');
  s.id = 'hr-lobby-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

const HOW_TO_STEPS = [
  'Streamer types <code>!start</code> to open the gates',
  'Each race shows 8 horses — type <code>1</code>-<code>8</code> in chat to back one',
  'Cheer with an emoji during the race — your bet horse gets a hype boost!',
  'Place payouts: <strong>1st = 1000</strong>, 2nd = 500, last = 100',
];

/**
 * Hero illustration: real painted galloping horse PNG (Leonardo-generated)
 * wrapped with a soft warm-yellow back-glow and an SVG dust-trail layer
 * for motion. CSS animations do all the bob/dust work — no scripts.
 */
const HORSE_DUST_SVG = `
<svg class="hr-lobby-horse-dust" viewBox="0 0 200 80" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" preserveAspectRatio="none">
  <ellipse cx="150" cy="40" rx="46" ry="9"  fill="rgba(255,255,255,0.34)"/>
  <ellipse cx="100" cy="52" rx="56" ry="7"  fill="rgba(255,255,255,0.24)"/>
  <ellipse cx="56"  cy="46" rx="40" ry="6"  fill="rgba(255,255,255,0.18)"/>
  <ellipse cx="22"  cy="58" rx="22" ry="4"  fill="rgba(255,255,255,0.12)"/>
</svg>
`.trim();

export function createLobby(stage: HTMLElement): LobbyHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'hr-lobby';
  el.setAttribute('data-scene', 'lobby');
  el.setAttribute('data-testid', 'hr-lobby');

  // --- Left column: top-10 leaderboard + how-to-play -----------------------
  const leftCol = document.createElement('div');
  leftCol.className = 'hr-lobby-col';
  let topLb: LobbyLeaderboardHandle | null = null;
  topLb = createLobbyLeaderboardTop(leftCol);
  const howto = createLobbyHowTo(leftCol);
  const howList = howto.el.querySelector('.sg-howto-list');
  if (howList) {
    howList.innerHTML = HOW_TO_STEPS.map((s) => `<li class="sg-howto-item">${s}</li>`).join('');
  }

  // --- Center column: hero horse + title -----------------------------------
  const center = document.createElement('div');
  center.className = 'hr-lobby-center';
  const horseWrapper = document.createElement('div');
  horseWrapper.className = 'hr-lobby-horse-wrap';
  horseWrapper.setAttribute('data-testid', 'hr-lobby-horse');
  const horseGlow = document.createElement('div');
  horseGlow.className = 'hr-lobby-horse-glow';
  horseWrapper.appendChild(horseGlow);
  const horseImg = document.createElement('img');
  horseImg.className = 'hr-lobby-horse';
  horseImg.src = titleHorseUrl;
  horseImg.alt = '';
  horseImg.decoding = 'async';
  horseWrapper.appendChild(horseImg);
  const dustEl = document.createElement('div');
  dustEl.innerHTML = HORSE_DUST_SVG;
  // Move the inner svg up one level so its class can position absolutely.
  const dustSvg = dustEl.firstElementChild;
  if (dustSvg) horseWrapper.appendChild(dustSvg);
  const titleSmall = document.createElement('div');
  titleSmall.className = 'hr-lobby-title hr-lobby-title-1';
  titleSmall.textContent = 'HORSE';
  const titleBig = document.createElement('div');
  titleBig.className = 'hr-lobby-title hr-lobby-title-2';
  titleBig.textContent = 'RACE';
  const sub = document.createElement('div');
  sub.className = 'hr-lobby-sub';
  sub.innerHTML = 'Type <code>1</code>-<code>8</code> in chat to bet on a horse — cheer with an emoji during the race!';
  const tagline = document.createElement('div');
  tagline.className = 'hr-lobby-tagline';
  tagline.textContent = 'WAITING FOR !START';
  center.appendChild(horseWrapper);
  center.appendChild(titleSmall);
  center.appendChild(titleBig);
  center.appendChild(sub);
  center.appendChild(tagline);

  // --- Right column: standings tail (ranks 11+) ----------------------------
  const rightCol = document.createElement('div');
  rightCol.className = 'hr-lobby-col';
  const tailLb = createLobbyLeaderboardTail(rightCol);

  el.appendChild(leftCol);
  el.appendChild(center);
  el.appendChild(rightCol);
  stage.appendChild(el);

  // The tagline auto-fits in case the streamer's font scales differently.
  requestAnimationFrame(() => fitText(tagline, { min: 14, max: 28 }));

  return {
    el,
    setLeaderboard(rows, allTime) {
      const useAllTime = allTime.length >= rows.length ? allTime : rows;
      topLb?.setRows(useAllTime);
      tailLb.setRows(useAllTime);
    },
    setVisible(v) {
      el.style.display = v ? 'grid' : 'none';
    },
  };
}
